package com.dotpx.ambient

import kotlinx.serialization.json.*

/**
 * Receiving-device state machine. All calls must be serialized by the host.
 * HTTP and durable storage are host capabilities; protocol and access policy live here.
 */
class PurchaseLinkDevice(
    private val platform: String,
    private val deviceName: String,
    serverHost: String,
    saved: String?,
    private val storage: PurchaseLinkStorage,
) {
    private val parser = PurchaseLinkCodeParser(serverHost)
    private var savedDevice = decodeSaved(saved)
    private var generation = 0L
    private var requestId = 0L
    private val requests = mutableMapOf<Long, Operation>()
    private val queue = ArrayDeque<PurchaseLinkRequest>()
    private var busy = false
    private var refreshing: Long? = null
    private var nextRefreshAt = 0L
    private var error = ""
    private var errorStatus = 0
    private var completedAction = ""
    private var closed = false

    init {
        require(platform in listOf("web", "windows", "linux"))
        require(deviceName.isNotBlank() && deviceName.length <= 80 && deviceName.none { it < ' ' || it == '\u007f' })
    }

    fun snapshot(nowMillis: Long): PurchaseLinkDeviceSnapshot {
        val value = savedDevice
        val linked = value?.access?.linked == true
        val premium = linked && value?.access?.premium == true && value.access.validUntil > nowMillis && !closed
        val pairing = value?.pairing?.takeIf { !linked && it.expiresAt > nowMillis && !closed }
        return PurchaseLinkDeviceSnapshot(
            linked = linked && !closed,
            pending = value != null && !linked && !closed,
            premium = premium,
            lifetime = premium && value?.access?.lifetime == true,
            busy = busy,
            validUntil = value?.access?.validUntil ?: 0,
            reason = value?.access?.reason.orEmpty(),
            error = error,
            errorStatus = errorStatus,
            qrSize = pairing?.qrSize ?: 0,
            qrModules = pairing?.qrModules.orEmpty(),
            linkUrl = pairing?.linkUrl,
            expiresAt = value?.pairing?.expiresAt ?: 0,
            nextRefreshAt = nextRefreshAt,
            hasDevice = value != null && !closed,
            accessExpired = linked && value?.access?.premium == true && value.access.validUntil <= nowMillis,
            completedAction = completedAction,
        )
    }

    fun create(nowMillis: Long) {
        if (closed || busy || savedDevice?.access?.linked == true) return
        busy = true
        completedAction = ""
        clearError()
        generation++
        refreshing = null
        nextRefreshAt = 0
        enqueue("create", "POST", "/v1/pairings", buildJsonObject {
            put("deviceName", deviceName); put("platform", platform)
        }.toString(), null)
    }

    fun refresh(nowMillis: Long) {
        val value = savedDevice ?: return
        if (closed || refreshing != null || busy) return
        nextRefreshAt = 0
        refreshing = enqueue("refresh", "GET", "/v1/device", null, value.deviceToken)
    }

    fun unlink(nowMillis: Long) {
        val value = savedDevice ?: return
        if (closed || busy) return
        busy = true
        completedAction = ""
        clearError()
        generation++
        refreshing = null
        nextRefreshAt = 0
        enqueue("unlink", "DELETE", "/v1/device", null, value.deviceToken)
    }

    /** Called at the host's scheduled wakeup. Snapshot itself is a pure expiry check. */
    fun tick(nowMillis: Long) {
        if (nextRefreshAt > 0 && nowMillis >= nextRefreshAt) refresh(nowMillis)
    }

    fun nextRequest(): PurchaseLinkRequest? = queue.removeFirstOrNull()
    internal fun peekRequest(): PurchaseLinkRequest? = queue.firstOrNull()

    fun complete(requestId: Long, status: Int, body: String, nowMillis: Long) {
        val operation = requests.remove(requestId) ?: return
        if (operation.kind == "cleanup") return
        val response = jsonObject(body)
        if (closed || operation.generation != generation) {
            if (operation.kind == "create" && status in 200..299) cleanup(response?.string("deviceToken"))
            return
        }
        when (operation.kind) {
            "create" -> {
                busy = false
                val token = response?.string("deviceToken")?.takeIf { linkingTokenPattern.matches(it) }
                val pairing = response?.let { decodePairing(it, strict = true) }?.takeIf { it.expiresAt > nowMillis }
                if (status !in 200..299 || token == null || pairing == null) {
                    cleanup(token)
                    fail(if (status in 200..299) "invalid_pairing" else "create_failed", status)
                } else {
                    val previous = savedDevice
                    val replacement = SavedDevice(token, pairing, null)
                    if (!persist(storage, encodeSaved(replacement))) {
                        cleanup(token)
                        fail("storage_unavailable", 0)
                    } else {
                        savedDevice = replacement
                        completedAction = "create"
                        clearError()
                        if (previous?.deviceToken != token) cleanup(previous?.deviceToken)
                    }
                }
                schedule(nowMillis)
            }
            "refresh" -> {
                refreshing = null
                if (status == 401 || status == 410) {
                    clearSaved()
                    if (error != "storage_unavailable") fail("authentication_expired", status)
                } else if (status !in 200..299) {
                    fail("refresh_failed", status)
                } else {
                    val access = response?.let(::decodeAccess)
                    if (access == null) fail("invalid_access", status)
                    else savedDevice?.let { previous ->
                        savedDevice = previous.copy(access = access, pairing = if (access.linked) null else previous.pairing)
                        if (persist(storage, encodeSaved(savedDevice!!))) clearError()
                        else fail("storage_unavailable", 0)
                    }
                }
                schedule(nowMillis)
            }
            "unlink" -> {
                busy = false
                if (status == 401 || status == 410 || (status in 200..299 && response?.boolean("unlinked") == true)) {
                    clearSaved()
                    completedAction = "unlink"
                } else if (status in 200..299) {
                    fail("invalid_response", status)
                } else fail("unlink_failed", status)
                schedule(nowMillis)
            }
        }
    }

    fun close() {
        closed = true
        generation++
        busy = false
        refreshing = null
        nextRefreshAt = 0
        queue.clear()
    }

    private fun enqueue(kind: String, method: String, path: String, body: String?, auth: String?): Long {
        val id = ++requestId
        requests[id] = Operation(kind, generation)
        queue.addLast(PurchaseLinkRequest(id, path, method, body, auth))
        return id
    }

    private fun cleanup(token: String?) {
        if (token != null && linkingTokenPattern.matches(token)) enqueue("cleanup", "DELETE", "/v1/device", null, token)
    }

    private fun schedule(nowMillis: Long) {
        nextRefreshAt = if (savedDevice == null || closed || refreshing != null) 0
        else nowMillis + if (savedDevice?.access?.linked == true) LINKED_INTERVAL else PENDING_INTERVAL
    }

    private fun clearSaved() {
        savedDevice = null
        generation++
        refreshing = null
        nextRefreshAt = 0
        clearError()
        if (!persist(storage, null)) fail("storage_unavailable", 0)
    }

    private fun clearError() { error = ""; errorStatus = 0 }
    private fun fail(code: String, status: Int) { error = code; errorStatus = status }

    private fun decodeSaved(text: String?): SavedDevice? {
        val value = jsonObject(text?.takeIf { it.length <= 65_536 }) ?: return null
        val token = value.string("deviceToken")?.takeIf { linkingTokenPattern.matches(it) } ?: return null
        val access = (value["access"] as? JsonObject)?.let { decodeAccess(it, legacy = true) }
        return SavedDevice(token, (value["pairing"] as? JsonObject)?.let { decodePairing(it, strict = false) }, access)
    }

    private fun decodePairing(value: JsonObject, strict: Boolean): Pairing? {
        val size = value.number("qrSize")?.takeIf { it in 1..177 } ?: return null
        val modules = value.string("qrModules")?.takeIf { it.length == size * size && it.all { c -> c == '0' || c == '1' } } ?: return null
        val expiresAt = value.millis("expiresAt") ?: return null
        val link = value.string("linkUrl")?.takeIf(parser::validPairingUrl)
        if (strict && link == null) return null
        return Pairing(size, modules, expiresAt, link)
    }

    private fun decodeAccess(value: JsonObject, legacy: Boolean = false): Access? {
        val state = value.string("state") ?: if (legacy && value.containsKey("linked")) {
            if (value.boolean("linked")) "linked" else "pending"
        } else return null
        if (state != "pending" && state != "linked") return null
        val linked = state == "linked"
        return Access(linked, linked && value.boolean("premium"), value.boolean("lifetime"),
            value.millis("validUntil") ?: 0, value.string("reason")?.takeIf { it.length <= 128 }.orEmpty())
    }

    private fun encodeSaved(value: SavedDevice): String = buildJsonObject {
        put("deviceToken", value.deviceToken)
        put("pairing", value.pairing?.let { pairing -> buildJsonObject {
            put("qrSize", pairing.qrSize); put("qrModules", pairing.qrModules); put("expiresAt", pairing.expiresAt)
            put("linkUrl", pairing.linkUrl?.let(::JsonPrimitive) ?: JsonNull)
        } } ?: JsonNull)
        put("access", value.access?.let { access -> buildJsonObject {
            put("state", if (access.linked) "linked" else "pending"); put("linked", access.linked)
            put("premium", access.premium); put("lifetime", access.lifetime); put("validUntil", access.validUntil)
            put("reason", access.reason)
        } } ?: JsonNull)
    }.toString()

    private data class SavedDevice(val deviceToken: String, val pairing: Pairing?, val access: Access?)
    private data class Pairing(val qrSize: Int, val qrModules: String, val expiresAt: Long, val linkUrl: String?)
    private data class Access(val linked: Boolean, val premium: Boolean, val lifetime: Boolean, val validUntil: Long, val reason: String)
    private data class Operation(val kind: String, val generation: Long)

    companion object {
        private const val PENDING_INTERVAL = 3_000L
        private const val LINKED_INTERVAL = 60_000L
    }
}
